import { Link, Navigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { FadeUp, MaskedLines } from '../components/anim';
import Lightbox from '../components/Lightbox';
import { CHAPTERS, UI } from '../data/portfolio';
import { useLang } from '../i18n';
import ProjectGate from '../components/ProjectGate';
import { useProtectedProjects, isUnlocked } from '../lib/protected';

const SPAN = { 3: 'md:col-span-3', 6: 'md:col-span-6' };

function rowsFor(photos) {
  const rows = [];
  let pair = [];
  let gi = 0;
  for (const p of photos) {
    const item = { im: p, gi };
    gi += 1;
    if (p.ar === 'band' || p.ar === 'wide') {
      if (pair.length) { rows.push(pair); pair = []; }
      rows.push([item]);
    } else if (pair.length === 0) {
      pair = [item];
    } else if (pair[0].im.ar === p.ar) {
      rows.push(pair); pair = [];
      rows[rows.length - 1].push(item);
      continue;
    } else {
      rows.push(pair); pair = [item];
    }
  }
  if (pair.length) rows.push(pair);
  return rows;
}

export default function Project() {
  const { slug, project: projectSlug } = useParams();
  const { t, lang } = useLang();
  const [lb, setLb] = useState(null);
  const protectedProjects = useProtectedProjects();
  const [unlocked, setUnlockedState] = useState(isUnlocked());

  useEffect(() => {
    if (lb != null) {
      if (window.__lenis) window.__lenis.stop();
      document.body.style.overflow = 'hidden';
    } else {
      if (window.__lenis) window.__lenis.start();
      document.body.style.overflow = '';
    }
  }, [lb]);

  const chapter = CHAPTERS.find((c) => c.slug === slug);
  if (!chapter) return <Navigate to="/work" replace />;
  const idx = chapter.projects.findIndex((p) => p.slug === projectSlug);
  if (idx === -1) return <Navigate to={`/work/${slug}`} replace />;
  const project = chapter.projects[idx];
  if (protectedProjects.includes(projectSlug) && !unlocked) {
    return <ProjectGate title={project.title} onUnlock={() => setUnlockedState(true)} />;
  }
  const prev = chapter.projects[(idx + chapter.projects.length - 1) % chapter.projects.length];
  const next = chapter.projects[(idx + 1) % chapter.projects.length];
  const rows = rowsFor(project.photos.slice(1));

  return (
    <main data-testid={`project-page-${projectSlug}`} className="px-6 pt-28 md:px-10 md:pt-40">
      <div className="mx-auto max-w-[1600px]">
        <FadeUp>
          <Link
            to={`/work/${slug}`}
            data-testid={`project-back-${projectSlug}`}
            className="group inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-ink/50 transition-colors hover:text-ink"
          >
            <span className="transition-transform duration-300 group-hover:-translate-x-1">←</span>
            {t(UI.backToChapter)} — {t(UI.chapterLabel)} {chapter.numeral}
          </Link>
        </FadeUp>

        <MaskedLines
          className="mt-10"
          delay={0.1}
          lines={[
            <h1
              key="t"
              data-testid={`project-heading-${projectSlug}`}
              className="text-[clamp(22px,3.5vw,38px)] font-light uppercase leading-tight tracking-[0.08em] text-balance"
            >
              {project.title}
            </h1>,
          ]}
        />
        <div className="mt-8 h-px bg-ink/25" />

        <div className="mt-8 grid gap-6 md:grid-cols-12 md:items-end">
          <FadeUp className="md:col-span-5 md:text-right">
            <p className="text-[10px] uppercase tracking-[0.25em] text-ink/60">{t(project.caption)}</p>
            {project.credits && (
              <div className="mt-2 space-y-1">
                {t(project.credits).split(' — ').map((line, i) => (
                  <p key={i} className="text-[10px] uppercase tracking-[0.25em] text-ink/40">{line}</p>
                ))}
              </div>
            )}
          </FadeUp>
          <FadeUp delay={0.08} className="md:col-span-7 md:text-right">
            <p className="text-[10px] uppercase tracking-[0.25em] text-ink/40">
              {t(UI.photoCount)} — {String(project.photos.length).padStart(2, '0')}
            </p>
          </FadeUp>
        </div>

        <FadeUp delay={0.12}>
          <div className="mt-8 max-w-[36rem] space-y-4">
            {project.texts.map((tx, i) => (
              <p key={i} className="text-[15px] leading-[1.55] text-ink/80 text-pretty md:text-[16px]">
                {t(tx)}
              </p>
            ))}
          </div>
        </FadeUp>

        {/* Hero photograph */}
        <FadeUp className="mt-12">
          <div
            className="h-[52vh] cursor-zoom-in overflow-hidden bg-white md:h-[82vh]"
            onClick={() => setLb(0)}
            data-testid={`project-hero-open-${projectSlug}`}
          >
            <img
              src={`/work/${project.photos[0].s}.jpg`}
              alt={project.title}
              data-testid={`project-hero-${projectSlug}`}
              className="h-full w-full object-cover transition-transform duration-700 ease-out hover:scale-[1.02]"
            />
          </div>
        </FadeUp>

        {/* All remaining photographs */}
        <div className="mt-6 space-y-4 md:mt-10 md:space-y-8">
          {rows.map((row, ri) => (
            <div key={ri} className="grid grid-cols-6 gap-4 md:gap-6">
              {row.map(({ im, gi }, i) => {
                const solo = row.length === 1;
                const ar = { std: 'aspect-[4/3]', tall: 'aspect-[3/4]', wide: 'aspect-[16/9]', band: 'aspect-[21/9]' }[im.ar];
                return (
                  <FadeUp
                    key={im.s}
                    delay={i * 0.08}
                    className={`col-span-6 ${solo && im.ar !== 'band' && im.ar !== 'wide' ? '' : SPAN[row.length === 2 ? 3 : 6]}`}
                  >
                    <div
                      className={`group cursor-zoom-in overflow-hidden bg-white ${ar}${solo && im.ar !== 'band' && im.ar !== 'wide' ? ' mx-auto w-full md:w-1/2' : ''}`}
                      onClick={() => setLb(gi + 1)}
                      data-testid={`project-photo-open-${im.s}`}
                    >
                      <img
                        src={`/work/${im.s}.jpg`}
                        alt={`${project.title} — ${gi + 2}`}
                        loading="lazy"
                        data-testid={`project-photo-${im.s}`}
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]"
                      />
                    </div>
                  </FadeUp>
                );
              })}
            </div>
          ))}
        </div>

        {/* Prev / next project */}
        <div className="mt-16 grid gap-4 border-t border-ink/15 py-12 md:mt-24 md:grid-cols-2 md:py-16">
          <Link
            to={`/work/${slug}/${prev.slug}`}
            data-testid={`project-prev-${prev.slug}`}
            className="group"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-ink/40">{t(UI.previous)}</div>
            <div className="mt-2 text-lg font-light uppercase tracking-[0.12em] transition-transform duration-500 group-hover:-translate-x-2 md:text-3xl">
              ← {prev.title}
            </div>
          </Link>
          <Link
            to={`/work/${slug}/${next.slug}`}
            data-testid={`project-next-${next.slug}`}
            className="group md:text-right"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-ink/40">{t(UI.next)}</div>
            <div className="mt-2 text-lg font-light uppercase tracking-[0.12em] transition-transform duration-500 group-hover:translate-x-2 md:text-3xl">
              {next.title} →
            </div>
          </Link>
        </div>
        <div className="pb-4 text-center text-[9px] uppercase tracking-[0.3em] text-ink/30">
          {lang === 'en' ? 'End of chapter' : 'Fin du chapitre'}
        </div>
      </div>

      <AnimatePresence>
        {lb != null && (
          <Lightbox
            photos={project.photos}
            index={lb}
            onClose={() => setLb(null)}
            onNav={(d) => setLb((v) => (v + d + project.photos.length) % project.photos.length)}
          />
        )}
      </AnimatePresence>
    </main>
  );
}
